import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Beaker, Droplets, Syringe, Info, ChevronDown, ArrowRight, ArrowLeft, AlertTriangle, Check } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';

const PEPTIDE_PRESETS = [
  { name: 'BPC-157', vial: 5, water: 2, dose: 250, unit: 'mcg', category: 'Healing', storage: 'Refrigerate after reconstitution. Use within 30 days.', notes: 'Most common healing peptide. Inject subQ near injury site.' },
  { name: 'TB-500', vial: 5, water: 2, dose: 2500, unit: 'mcg', category: 'Healing', storage: 'Refrigerate. Use within 30 days.', notes: 'Systemic recovery. Can inject anywhere subQ.' },
  { name: 'CJC-1295 / Ipamorelin', vial: 5, water: 2.5, dose: 300, unit: 'mcg', category: 'GH', storage: 'Refrigerate. Use within 21 days.', notes: 'Take on empty stomach. Best before bed.' },
  { name: 'Semaglutide', vial: 5, water: 2, dose: 500, unit: 'mcg', category: 'Metabolic', storage: 'Refrigerate. Use within 28 days.', notes: 'Titrate slowly. Once weekly dosing.' },
  { name: 'GHK-Cu', vial: 5, water: 2, dose: 200, unit: 'mcg', category: 'Healing', storage: 'Refrigerate. Use within 30 days.', notes: 'Copper peptide for skin, hair, and tissue.' },
  { name: 'Tesamorelin', vial: 2, water: 2, dose: 2000, unit: 'mcg', category: 'GH / Fat Loss', storage: 'Refrigerate. Use within 14 days.', notes: 'FDA-approved for visceral fat reduction.' },
  { name: 'PT-141', vial: 10, water: 2, dose: 2000, unit: 'mcg', category: 'Libido', storage: 'Refrigerate. Use within 30 days.', notes: 'Use 2-4 hours before activity. Do not exceed 2x/week.' },
  { name: 'MOTS-C', vial: 10, water: 2, dose: 5000, unit: 'mcg', category: 'Metabolic', storage: 'Refrigerate. Use within 30 days.', notes: 'Exercise mimetic peptide. 5mg 3x/week.' },
  { name: 'Epitalon', vial: 10, water: 2, dose: 5000, unit: 'mcg', category: 'Anti-Aging', storage: 'Refrigerate. Use within 30 days.', notes: 'Telomerase activation. 10-day cycles.' },
];

const STEPS = [
  { num: 1, title: 'Gather Supplies', desc: 'You need: peptide vial, bacteriostatic water (BAC), alcohol swabs, insulin syringes (1mL/100 units).', icon: Beaker },
  { num: 2, title: 'Clean Everything', desc: 'Swab the top of both the peptide vial and BAC water vial with alcohol. Let dry 10 seconds.', icon: Droplets },
  { num: 3, title: 'Draw BAC Water', desc: 'Using a syringe, draw the calculated amount of BAC water. Inject AIR into the BAC vial first (equal to water volume) to equalize pressure.', icon: Syringe },
  { num: 4, title: 'Add Water to Peptide', desc: 'Inject BAC water into the peptide vial SLOWLY. Aim the stream at the glass wall — never spray directly onto the powder. Let it drip down gently.', icon: Droplets },
  { num: 5, title: 'Swirl Gently', desc: 'Roll the vial between your palms. NEVER shake. The peptide should dissolve within 1-3 minutes. If not, refrigerate and check in 30 min.', icon: Beaker },
  { num: 6, title: 'Draw Your Dose', desc: 'Using a fresh insulin syringe, draw your calculated dose in units. Inject subcutaneously at a 45° angle into belly fat or near injury site.', icon: Syringe },
];

const SyringeVisual = ({ units, maxUnits = 100 }) => {
  const pct = Math.min(Math.max((units / maxUnits) * 100, 0), 100);
  return (
    <div className="relative w-full max-w-[320px] mx-auto my-6" data-testid="syringe-visual">
      <div className="relative h-10 bg-zinc-800 border-2 border-zinc-600 rounded-full overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#E63946] to-[#E63946]/70 transition-all duration-700 ease-out rounded-full" style={{ width: `${pct}%` }} />
        {[10, 20, 30, 40, 50, 60, 70, 80, 90].map(t => (
          <div key={t} className="absolute top-0 bottom-0 w-px bg-zinc-600/50" style={{ left: `${t}%` }} />
        ))}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white text-sm font-bold drop-shadow-lg font-heading">{units > 0 ? `${units.toFixed(1)} units` : '—'}</span>
        </div>
      </div>
      <div className="flex justify-between mt-1 px-1">
        {[0, 25, 50, 75, 100].map(v => (
          <span key={v} className="text-zinc-600 text-xs">{v}</span>
        ))}
      </div>
    </div>
  );
};

export default function ReconstitutionGuidePage() {
  const [preset, setPreset] = useState('');
  const [vialMg, setVialMg] = useState('');
  const [waterMl, setWaterMl] = useState('');
  const [doseMcg, setDoseMcg] = useState('');
  const [saved, setSaved] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const { saveResult } = useResults();

  const handlePreset = (name) => {
    const p = PEPTIDE_PRESETS.find(x => x.name === name);
    if (p) {
      setPreset(name);
      setVialMg(p.vial);
      setWaterMl(p.water);
      setDoseMcg(p.dose);
    }
  };

  const calc = useMemo(() => {
    const v = parseFloat(vialMg);
    const w = parseFloat(waterMl);
    const d = parseFloat(doseMcg);
    if (!v || !w || !d || v <= 0 || w <= 0 || d <= 0) return null;
    const totalMcg = v * 1000;
    const concentrationMcgPerMl = totalMcg / w;
    const doseVolumeMl = d / concentrationMcgPerMl;
    const doseUnits = doseVolumeMl * 100;
    const dosesPerVial = Math.floor(totalMcg / d);
    const concentrationMcgPerUnit = totalMcg / (w * 100);
    return { totalMcg, concentrationMcgPerMl, doseVolumeMl, doseUnits, dosesPerVial, concentrationMcgPerUnit };
  }, [vialMg, waterMl, doseMcg]);

  const selectedPreset = PEPTIDE_PRESETS.find(p => p.name === preset);

  const handleSave = () => {
    if (!calc) return;
    saveResult('reconstitution', {
      value: `${calc.doseUnits.toFixed(1)} units`,
      label: preset || 'Custom',
      vialMg, waterMl, doseMcg,
      dosesPerVial: calc.dosesPerVial,
    });
    setSaved(true);
  };

  return (
    <ToolSEOWrapper toolKey="reconstitution-guide">
    <main className="pt-20 min-h-screen" data-testid="reconstitution-guide-page">
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl mx-auto">
          <Link to="/tools" className="text-zinc-500 text-xs font-heading uppercase tracking-wider hover:text-white transition-colors inline-flex items-center gap-1 mb-6" data-testid="back-to-tools">
            <ArrowLeft className="w-3 h-3" /> All Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E63946]/10 border border-[#E63946]/30">
              <Beaker className="w-5 h-5 text-[#E63946]" />
            </div>
            <h1 className="text-3xl sm:text-4xl text-white font-heading" data-testid="page-title">Reconstitution Guide</h1>
          </div>
          <p className="text-zinc-400 text-sm mb-8">Step-by-step guide to reconstituting peptides with BAC water. Calculate exact syringe draw volumes for precise dosing.</p>

          {/* Step-by-Step Guide Toggle */}
          <button onClick={() => setShowSteps(!showSteps)}
            className="w-full flex items-center justify-between bg-zinc-900/80 border border-zinc-800 hover:border-[#E63946]/30 p-4 mb-6 transition-colors"
            data-testid="toggle-steps-btn">
            <span className="text-white font-heading text-sm uppercase tracking-wider flex items-center gap-2">
              <Info className="w-4 h-4 text-[#E63946]" /> How to Reconstitute Peptides (6 Steps)
            </span>
            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showSteps ? 'rotate-180' : ''}`} />
          </button>

          {showSteps && (
            <div className="grid gap-3 mb-8" data-testid="steps-section">
              {STEPS.map(s => (
                <div key={s.num} className="flex items-start gap-4 bg-zinc-900/80 border border-zinc-800 p-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#E63946] text-black font-heading text-sm font-bold flex-shrink-0">{s.num}</div>
                  <div>
                    <h3 className="text-white font-heading text-base mb-1">{s.title}</h3>
                    <p className="text-zinc-400 text-sm">{s.desc}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-3 bg-[#f59e0b]/5 border border-[#f59e0b]/20 p-4">
                <AlertTriangle className="w-5 h-5 text-[#f59e0b] flex-shrink-0 mt-0.5" />
                <p className="text-zinc-300 text-sm"><strong className="text-[#f59e0b]">Important:</strong> Always use bacteriostatic water (BAC), not sterile water. BAC contains 0.9% benzyl alcohol which prevents bacterial growth. Store reconstituted vials in the refrigerator.</p>
              </div>
            </div>
          )}

          {/* Preset Selector */}
          <div className="mb-6">
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-2 font-heading">Quick Select Peptide</label>
            <div className="flex flex-wrap gap-2">
              {PEPTIDE_PRESETS.map(p => (
                <button key={p.name} onClick={() => handlePreset(p.name)}
                  className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wider border transition-colors ${
                    preset === p.name ? 'text-[#E63946] border-[#E63946]/40 bg-[#E63946]/5' : 'text-zinc-500 border-zinc-700 hover:text-white hover:border-zinc-500'
                  }`}
                  data-testid={`preset-${p.name.toLowerCase().replace(/[\s\/]+/g, '-')}`}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          {/* Calculator Inputs */}
          <div className="bg-zinc-900/80 border border-zinc-800 p-6 mb-6" data-testid="calculator-section">
            <h2 className="text-white font-heading text-lg mb-4 flex items-center gap-2">
              <Syringe className="w-5 h-5 text-[#E63946]" /> Dose Calculator
            </h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Vial Size (mg)</label>
                <input type="number" value={vialMg} onChange={e => { setVialMg(e.target.value); setPreset(''); }}
                  placeholder="e.g. 5" min="0" step="0.1"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-vial-mg" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">BAC Water (mL)</label>
                <input type="number" value={waterMl} onChange={e => { setWaterMl(e.target.value); setPreset(''); }}
                  placeholder="e.g. 2" min="0" step="0.1"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-water-ml" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Dose (mcg)</label>
                <input type="number" value={doseMcg} onChange={e => { setDoseMcg(e.target.value); setPreset(''); }}
                  placeholder="e.g. 250" min="0" step="1"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-dose-mcg" />
              </div>
            </div>

            {calc && (
              <div data-testid="results-section">
                <SyringeVisual units={calc.doseUnits} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-zinc-800 p-3 text-center border border-zinc-700">
                    <p className="text-xl font-heading text-[#E63946]" data-testid="result-units">{calc.doseUnits.toFixed(1)}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Units to Draw</p>
                  </div>
                  <div className="bg-zinc-800 p-3 text-center border border-zinc-700">
                    <p className="text-xl font-heading text-[#00F0FF]" data-testid="result-volume">{calc.doseVolumeMl.toFixed(3)}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">mL per Dose</p>
                  </div>
                  <div className="bg-zinc-800 p-3 text-center border border-zinc-700">
                    <p className="text-xl font-heading text-green-500" data-testid="result-doses">{calc.dosesPerVial}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Doses per Vial</p>
                  </div>
                  <div className="bg-zinc-800 p-3 text-center border border-zinc-700">
                    <p className="text-xl font-heading text-white" data-testid="result-concentration">{Math.round(calc.concentrationMcgPerMl)}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">mcg/mL</p>
                  </div>
                </div>

                {/* Formula explanation */}
                <div className="bg-zinc-800/50 border border-zinc-700 p-4 mb-4">
                  <p className="text-zinc-500 text-xs font-heading uppercase tracking-wider mb-2">How It's Calculated</p>
                  <div className="text-zinc-400 text-xs space-y-1 font-mono">
                    <p>Concentration = {vialMg}mg x 1000 / {waterMl}mL = <strong className="text-white">{Math.round(calc.concentrationMcgPerMl)} mcg/mL</strong></p>
                    <p>Dose Volume = {doseMcg}mcg / {Math.round(calc.concentrationMcgPerMl)} mcg/mL = <strong className="text-white">{calc.doseVolumeMl.toFixed(3)} mL</strong></p>
                    <p>Units to Draw = {calc.doseVolumeMl.toFixed(3)} mL x 100 = <strong className="text-[#E63946]">{calc.doseUnits.toFixed(1)} units</strong></p>
                  </div>
                </div>

                {selectedPreset && (
                  <div className="bg-zinc-800/50 border border-[#E63946]/20 p-4 mb-4" data-testid="preset-info">
                    <h4 className="text-white text-sm font-heading mb-2">{selectedPreset.name} — {selectedPreset.category}</h4>
                    <p className="text-zinc-400 text-xs mb-1">{selectedPreset.notes}</p>
                    <p className="text-zinc-500 text-xs"><strong>Storage:</strong> {selectedPreset.storage}</p>
                  </div>
                )}

                <SaveResultButton onSave={handleSave} saved={saved} />
              </div>
            )}

            {!calc && (
              <div className="text-center py-8 text-zinc-600 text-sm">
                Enter vial size, BAC water volume, and desired dose to calculate.
              </div>
            )}
          </div>

          {/* Quick Reference Table */}
          <div className="bg-zinc-900/80 border border-zinc-800 p-6" data-testid="reference-table">
            <h3 className="text-white font-heading text-base mb-4">Quick Reference — Common Reconstitutions</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left p-2 text-zinc-400 text-xs uppercase tracking-wider font-heading">Peptide</th>
                    <th className="text-center p-2 text-zinc-400 text-xs uppercase tracking-wider font-heading">Vial</th>
                    <th className="text-center p-2 text-zinc-400 text-xs uppercase tracking-wider font-heading">BAC Water</th>
                    <th className="text-center p-2 text-zinc-400 text-xs uppercase tracking-wider font-heading">Dose</th>
                    <th className="text-center p-2 text-zinc-400 text-xs uppercase tracking-wider font-heading">Draw</th>
                    <th className="text-center p-2 text-zinc-400 text-xs uppercase tracking-wider font-heading">Doses/Vial</th>
                  </tr>
                </thead>
                <tbody>
                  {PEPTIDE_PRESETS.map(p => {
                    const totalMcg = p.vial * 1000;
                    const concPerMl = totalMcg / p.water;
                    const volMl = p.dose / concPerMl;
                    const units = volMl * 100;
                    const doses = Math.floor(totalMcg / p.dose);
                    return (
                      <tr key={p.name} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors cursor-pointer" onClick={() => handlePreset(p.name)}>
                        <td className="p-2 text-white font-medium">{p.name}</td>
                        <td className="p-2 text-center text-zinc-300">{p.vial}mg</td>
                        <td className="p-2 text-center text-zinc-300">{p.water}mL</td>
                        <td className="p-2 text-center text-zinc-300">{p.dose}{p.unit}</td>
                        <td className="p-2 text-center text-[#E63946] font-heading">{units.toFixed(1)}u</td>
                        <td className="p-2 text-center text-green-500 font-heading">{doses}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Related Links */}
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/tools/peptide-dosing" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="link-peptide-dosing">
              Peptide Dosing Calculator <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/tools/peptide-calculator" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors">
              Original Peptide Calculator <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/blog?category=Peptides" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors">
              Peptide Guides <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
